import { createGlobalState, useStorage } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";

export type ThemeMode = "dark" | "light" | "system";

export const useTheme = createGlobalState(() => {
  const mode = useStorage<ThemeMode>("theme", "system");
  const theme = ref<Omit<ThemeMode, "system">>("");

  const updateTheme = (isDark: boolean) => {
    if (isDark) {
      document.body.setAttribute("theme", "dark");
    } else {
      document.body.setAttribute("theme", "light");
    }
  };

  watchEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "theme") {
          const value = document.body.getAttribute("theme");
          if (value) {
            theme.value = value;
          }
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    return () => {
      observer.disconnect();
    };
  });

  const setMode = (value: ThemeMode) => {
    mode.value = value;

    if (value === "dark") {
      updateTheme(true);
    } else if (value === "light") {
      updateTheme(false);
    } else {
      const systemPreference = globalThis.matchMedia("(prefers-color-scheme: dark)").matches;
      updateTheme(systemPreference);
    }
  };

  const handleSystemPreferenceChange = (e: MediaQueryListEvent) => {
    if (mode.value === "system") {
      updateTheme(e.matches);
    }
  };

  onMounted(() => {
    const matcher = globalThis.matchMedia("(prefers-color-scheme: dark)");
    matcher.addEventListener("change", handleSystemPreferenceChange);
    setMode(mode.value);
  });

  onUnmounted(() => {
    globalThis
      .matchMedia("(prefers-color-scheme: dark)")
      .removeEventListener("change", handleSystemPreferenceChange);
  });

  return {
    mode,
    setMode,
    theme,
    isDark: computed(() => theme.value === "dark"),
  };
});

export const isDark = computed(() => {
  const theme = useTheme();
  return theme.isDark.value;
});

export const theme = computed(() => {
  const theme = useTheme();
  return theme.theme.value ?? "light";
});
